import { ApiService } from './api';
import { ApiResponse } from '../types/auth';
import {
  RestaurantResponse,
  RestaurantDetailsResponse,
  RestaurantsRequest,
  ChangeRestaurantApplicationStatusRequest,
  ChangeRestaurantBalanceRequest,
  RestaurantCommissionConfig,
  SetRestaurantCommissionRequest,
  RestaurantTransactionsResponse,
  CreateRestaurantTransactionRequest,
  CreateRestaurantTransactionResponse
} from '../types/restaurants';

export class RestaurantsService {
  private apiService: ApiService;

  constructor() {
    this.apiService = ApiService.getInstance();
  }

  async getRestaurants(request: RestaurantsRequest): Promise<ApiResponse<RestaurantResponse[]>> {
    try {
      const params = new URLSearchParams({
        searchQuery: request.searchQuery,
        page: request.page.toString(),
        pageSize: request.pageSize.toString(),
      });

      if (request.applicationStatus) {
        params.append('applicationStatus', request.applicationStatus);
      }

      const response = await this.apiService.get<RestaurantResponse[]>(
        `/admin/restaurants?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch restaurants',
        success: false
      };
    }
  }

  async getRestaurantDetails(restaurantId: number): Promise<ApiResponse<RestaurantDetailsResponse>> {
    try {
      const response = await this.apiService.get<RestaurantDetailsResponse>(
        `/admin/restaurants/${restaurantId}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch restaurant details',
        success: false
      };
    }
  }

  async changeApplicationStatus(request: ChangeRestaurantApplicationStatusRequest): Promise<ApiResponse<void>> {
    try {
      await this.apiService.post<void>(
        '/admin/restaurants/change-application-status',
        request
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to change application status',
        success: false
      };
    }
  }

  async changeBalance(request: ChangeRestaurantBalanceRequest): Promise<ApiResponse<void>> {
    try {
      const formData = new URLSearchParams();
      formData.append('balance', request.balance.toString());

      await this.apiService.post<void>(
        `/admin/restaurants/${request.restaurantId}/change-balance`,
        formData.toString(),
        'application/x-www-form-urlencoded'
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to change balance',
        success: false
      };
    }
  }

  async getAllRestaurantCommissions(): Promise<ApiResponse<RestaurantCommissionConfig[]>> {
    try {
      const response = await this.apiService.get<RestaurantCommissionConfig[]>(
        '/admin/restaurants/commission'
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch restaurant commissions',
        success: false
      };
    }
  }

  async getRestaurantCommission(restaurantId: number): Promise<ApiResponse<RestaurantCommissionConfig>> {
    try {
      const response = await this.apiService.get<RestaurantCommissionConfig>(
        `/admin/restaurants/${restaurantId}/commission`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch restaurant commission',
        success: false
      };
    }
  }

  async setRestaurantCommission(request: SetRestaurantCommissionRequest): Promise<ApiResponse<RestaurantCommissionConfig>> {
    try {
      const response = await this.apiService.post<RestaurantCommissionConfig>(
        '/admin/restaurants/commission',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to set restaurant commission',
        success: false
      };
    }
  }

  async deleteRestaurantCommission(restaurantId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.delete<void>(
        `/admin/restaurants/${restaurantId}/commission`
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to delete restaurant commission',
        success: false
      };
    }
  }

  async getRestaurantTransactions(
    restaurantId: number,
    page: number = 0,
    pageSize: number = 20
  ): Promise<ApiResponse<RestaurantTransactionsResponse>> {
    try {
      const params = new URLSearchParams({
        restaurantId: restaurantId.toString(),
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.apiService.get<RestaurantTransactionsResponse>(
        `/admin/restaurant-transactions?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch restaurant transactions',
        success: false
      };
    }
  }

  async createRestaurantTransaction(
    request: CreateRestaurantTransactionRequest
  ): Promise<ApiResponse<CreateRestaurantTransactionResponse>> {
    try {
      const response = await this.apiService.post<CreateRestaurantTransactionResponse>(
        '/admin/restaurant-transactions',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to create restaurant transaction',
        success: false
      };
    }
  }

  async deleteRestaurantTransaction(transactionId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.delete<void>(
        `/admin/restaurant-transactions/${transactionId}`
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to delete restaurant transaction',
        success: false
      };
    }
  }

  async updateRestaurant(restaurantId: number, request: {
    name?: string;
    description?: string;
    isOpen?: boolean;
    workStartTime?: string;
    workEndTime?: string;
    bannerId?: string;
  }): Promise<ApiResponse<RestaurantResponse>> {
    try {
      const response = await this.apiService.put<RestaurantResponse>(
        `/admin/restaurants/${restaurantId}`,
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to update restaurant',
        success: false
      };
    }
  }

  async updateFood(restaurantId: number, request: {
    foodId: number;
    categoryId?: number;
    name?: string;
    description?: string;
    price?: number;
    isAvailable?: boolean;
    foodImageIds?: string[];
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.apiService.put<any>(
        `/admin/restaurants/${restaurantId}/foods`,
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to update food',
        success: false
      };
    }
  }
}

export const restaurantsService = new RestaurantsService();
